<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useForm } from 'vee-validate';
import * as yup from 'yup';
import { useToast } from 'primevue/usetoast';
import tinycolor from 'tinycolor2';
import { callAPI, DRUPAL_API_ROUTE } from '@/service/DrupalService';
import Qrcodes from '@/views/Qrcodes.vue';
import { useMainStore } from '@/stores/store.js';
import { useRoute, useRouter } from 'vue-router';

const { t } = useI18n();
const toast = useToast();
const store = useMainStore();
const route = useRoute();
const router = useRouter();

const props = defineProps({
    visible: {
        type: Boolean,
        required: true
    },
    // QR code to edit (tags and note only); null to create one.
    qrcode: {
        type: Object,
        default: null
    }
});
const emit = defineEmits(['update:visible', 'dynamic', 'created', 'updated']);

const localVisible = computed({
    get: () => props.visible,
    set: (value) => emit('update:visible', value)
});

const user = ref(JSON.parse(localStorage.getItem('user')));

// Drawer breadcrumb, as on the short link drawer.
const breadcrumbHome = { icon: 'pi pi-home' };
const breadcrumbItems = [{ label: t('common.labels.qrcode'), icon: 'pi pi-qrcode' }];

// Free-form content to encode (URL, text, anything), capped to stay within the
// QR code byte capacity (correct level H).
const schema = yup.object({
    content: yup.string().required().max(1000),
    note: yup.string().max(255)
});

const { defineField, handleSubmit, resetForm, setValues, errors } = useForm({
    validationSchema: schema
});

const [content] = defineField('content');
const [note] = defineField('note');

// null = choice screen, 'static' = static QR generator, 'edit' = existing QR code.
const mode = ref(null);
// Debounced value handed to the generator to avoid one request per keystroke.
const qrContent = ref('');
// Styling captured from the Qrcodes component (v-model).
const qrcodeTemplate = ref('');
// Optional tags and note of the QR code (Administration section).
const administrationToggle = ref(false);
const tags = ref([]);
const tagsList = ref([]);
const tagsLoading = ref(false);
// Post-creation modal state.
const resultDialog = ref(false);
const createdQRCode = ref(null);
const additional = ref(false);
const generateLoading = ref(false);
// Stored image of the edited QR code (base64).
const editImageUrl = ref(null);

const downloadItems = [
    { label: 'PNG', icon: 'pi pi-download', command: () => downloadQRCode('png') },
    { label: 'SVG', icon: 'pi pi-download', command: () => downloadQRCode('svg'), disabled: !user.value.permissions.generate_svg_qrcode }
];

const getTextColor = (value) => (tinycolor(value).isLight() ? '#000' : '#fff');

const fetchTags = async () => {
    tagsLoading.value = true;
    const response = await callAPI(DRUPAL_API_ROUTE.TAGS);
    tagsList.value = Array.isArray(response.data) ? response.data : [];
    tagsLoading.value = false;
};

let debounce;
watch(content, (value) => {
    clearTimeout(debounce);
    debounce = setTimeout(() => {
        qrContent.value = value;
    }, 400);
});

function formatQRCodeHexString(input) {
    if (!input || typeof input !== 'string') return '#000';
    const color = input.startsWith('#') ? input : `#${input}`;
    return /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(color) ? color : '#000';
}

// Style chosen in the Qrcodes component: a template, or the custom options.
// The API renders and stores the PNG and SVG with it (the style is not kept).
const buildStyle = () => {
    const tpl = qrcodeTemplate.value || {};

    return {
        template: tpl.QRCodesTemplateSelected?.id || false,
        title: tpl.QRCodeFrameTitle || false,
        color: formatQRCodeHexString(tpl.QRCodeColor) || false,
        logo: tpl.QRCodeLogo || false,
        shape: tpl.QRCodeShape || false,
        eyes: tpl.QRCodeEyes || false,
        po_tl: formatQRCodeHexString(tpl.QRCodePOTL) || false,
        pi_tl: formatQRCodeHexString(tpl.QRCodePITL) || false,
        po_tr: formatQRCodeHexString(tpl.QRCodePOTR) || false,
        pi_tr: formatQRCodeHexString(tpl.QRCodePITR) || false,
        po_bl: formatQRCodeHexString(tpl.QRCodePOBL) || false,
        pi_bl: formatQRCodeHexString(tpl.QRCodePIBL) || false,
        frame: tpl.QRCodeFrame || false,
        frame_color: formatQRCodeHexString(tpl.QRCodeFrameColor) || false,
        frame_text_color: tpl.QRCodeFrameTextColor || false
    };
};

function chooseStatic() {
    mode.value = 'static';
}

// Edit mode: the QR code itself is read-only, only its tags and note change.
async function openEdit(qrcode) {
    mode.value = 'edit';
    setValues({ content: qrcode.content, note: qrcode.note || '' });
    tags.value = (qrcode.tags || []).map((tag) => tag.id);
    administrationToggle.value = tags.value.length > 0 || !!qrcode.note;
    if (administrationToggle.value && !tagsList.value.length) fetchTags();

    const response = await callAPI(DRUPAL_API_ROUTE.QRCODES_STATIC_GET, { uuid: qrcode.id });
    editImageUrl.value = response.data?.png?.data || null;
}

watch(() => props.visible, (visible) => {
    if (visible && props.qrcode) openEdit(props.qrcode);
});

const onUpdate = handleSubmit(async () => {
    if (generateLoading.value) return;
    generateLoading.value = true;
    try {
        const response = await callAPI(DRUPAL_API_ROUTE.QRCODES_STATIC_UPDATE, { uuid: props.qrcode.id }, {
            journal: user.value.permissions.save_log,
            tags: administrationToggle.value ? tags.value : [],
            note: administrationToggle.value ? (note.value || '') : ''
        });

        if (response.code || !response.data?.id) {
            toast.add({ severity: 'error', summary: t('menu.qrcodes'), detail: t('qrcodes.update.error'), life: 5000 });
            return;
        }

        toast.add({ severity: 'success', summary: t('menu.qrcodes'), detail: t('qrcodes.update.success'), life: 5000 });
        emit('updated', {
            ...props.qrcode,
            note: response.data.note,
            tags: tagsList.value.filter((tag) => tags.value.includes(tag.id))
        });
        localVisible.value = false;
    } finally {
        generateLoading.value = false;
    }
});

function chooseDynamic() {
    // A dynamic QR code is backed by a short link: hand off to the short link flow.
    emit('dynamic');
    localVisible.value = false;
}

// Saves the QR code: the API stores the row and the rendered PNG/SVG.
const onCreate = handleSubmit(async () => {
    if (generateLoading.value) return;
    generateLoading.value = true;
    try {
        const response = await callAPI(DRUPAL_API_ROUTE.QRCODES_STATIC_CREATE, {}, {
            journal: user.value.permissions.save_log,
            content: content.value,
            tags: administrationToggle.value ? tags.value : [],
            note: administrationToggle.value ? (note.value || '') : '',
            ...buildStyle()
        });

        if (response.code || !response.data?.id) {
            toast.add({ severity: 'error', summary: t('qrcodes.create.title'), detail: t('qrcodes.create.error'), life: 5000 });
            return;
        }

        createdQRCode.value = response.data;
        additional.value = false;
        resultDialog.value = true;
        // The result modal stays open: the drawer closes and the list reloads
        // (or is opened when on another page).
        localVisible.value = false;
        store.notifyQrcodeCreated();
        // The list opens (or switches) on the static QR codes.
        if (route.name !== 'qrcodes') router.push({ name: 'qrcodes', query: { mode: 'static' } });
        emit('created', response.data);
    } finally {
        generateLoading.value = false;
    }
});

// Downloads the stored image through its private Drupal copy, like the short links.
async function downloadQRCode(format) {
    if (!createdQRCode.value?.id) return;
    const response = await callAPI(DRUPAL_API_ROUTE.QRCODES_STATIC_GET, { uuid: createdQRCode.value.id });
    const url = response.data?.[format]?.url;
    if (url) window.open(url, '_blank');
}

function cancel() {
    localVisible.value = false;
}

// Keeps the creation state while the result modal is shown (download, template).
function onDrawerHide() {
    if (!resultDialog.value) resetState();
}

function resetState() {
    clearTimeout(debounce);
    mode.value = null;
    qrContent.value = '';
    qrcodeTemplate.value = '';
    administrationToggle.value = false;
    editImageUrl.value = null;
    tags.value = [];
    createdQRCode.value = null;
    additional.value = false;
    resultDialog.value = false;
    resetForm();
}
</script>

<template>
    <Drawer :blockScroll="true" @hide="onDrawerHide" v-model:visible="localVisible"
        class="!w-full md:!w-80 lg:!w-[35rem]">
        <template #header>
            <div class="flex items-center gap-2">
                <Breadcrumb :home="breadcrumbHome" :model="breadcrumbItems" class="!p-0">
                    <template #item="{ item }">
                        <span v-if="item.label" class="text-color mr-2">{{ item.label }}</span>
                        <span :class="item.icon"></span>
                    </template>
                    <template #separator><i class="pi pi-angle-right"></i></template>
                </Breadcrumb>
            </div>
        </template>

        <!-- Choice screen -->
        <div v-if="!mode && !qrcode" class="flex flex-col gap-4">
            <button type="button" @click="chooseDynamic"
                class="text-left p-4 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-(--surface-hover)! dark:hover:bg-gray-700 transition-colors cursor-pointer">
                <div class="flex items-center gap-2 font-semibold mb-1"><i class="pi pi-link"></i>{{
                    t('qrcodes.create.dynamic.title') }}</div>
                <p class="text-sm text-gray-500 m-0">{{ t('qrcodes.create.dynamic.description') }}</p>
            </button>

            <button type="button" @click="chooseStatic"
                class="text-left p-4 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-(--surface-hover)! dark:hover:bg-gray-700 transition-colors cursor-pointer">
                <div class="flex items-center gap-2 font-semibold mb-1"><i class="pi pi-qrcode"></i>{{
                    t('qrcodes.create.static.title') }}</div>
                <p class="text-sm text-gray-500 m-0">{{ t('qrcodes.create.static.description') }}</p>
            </button>
        </div>

        <!-- Static generator: full customization, free-form content -->
        <div v-else-if="mode === 'static' || mode === 'edit'" class="flex flex-col gap-4">
            <div v-if="mode === 'edit'" class="flex flex-col items-center gap-4">
                <img v-if="editImageUrl" class="max-w-64" :src="editImageUrl" alt="QR Code" />
                <Skeleton v-else width="16rem" height="16rem" />
                <p class="text-center font-semibold break-all m-0">{{ content }}</p>
            </div>
            <div v-else class="flex flex-col gap-2">
                <label for="qrcode_content">{{ t('qrcodes.create.content') }}</label>
                <Textarea id="qrcode_content" v-model="content" rows="2" autoResize maxlength="1000"
                    :class="{ 'p-invalid': errors.content }" :placeholder="t('qrcodes.create.content_placeholder')" />
                <small class="text-gray-500 self-end">{{ (content || '').length }}/1000</small>
                <Message v-if="errors.content" severity="error" size="small" variant="simple">{{ errors.content }}
                </Message>
            </div>
            <Qrcodes v-if="mode === 'static'" v-model="qrcodeTemplate" :shortlink="qrContent" :hideDownload="true" />
            <div class="flex flex-col space-y-6">
                <div class="flex items-center space-x-2">
                    <ToggleSwitch v-model="administrationToggle" :disabled="!user.permissions.add_tag"
                        @update:modelValue="val => val && !tagsList.length && fetchTags()" />
                    <span class="text-xl">{{ t('shortlink.administration.title') }}</span><i class="pi pi-tag me-auto"></i>
                </div>
                <div v-if="administrationToggle" class="flex flex-row space-x-10">
                    <div class="flex flex-col gap-2 w-1/3">
                        <label for="qrcode_tags">{{ t('shortlink.administration.tags') }}</label>
                        <MultiSelect id="qrcode_tags" v-model="tags" :options="tagsList" :loading="tagsLoading"
                            optionValue="id" optionLabel="name" filter display="chip">
                            <template #option="slotProps">
                                <Tag
                                    :style="{ background: slotProps.option.color, color: getTextColor(slotProps.option.color) }">
                                    <span class="text-xs font-medium">{{ slotProps.option.name }}</span>
                                </Tag>
                            </template>
                        </MultiSelect>
                    </div>
                    <div class="flex flex-col gap-2 w-2/3">
                        <label for="qrcode_note">{{ t('shortlink.administration.comment') }}</label>
                        <InputText id="qrcode_note" v-model="note" maxlength="255"
                            :class="{ 'p-invalid': errors.note }" />
                        <Message v-if="errors.note" severity="error" size="small" variant="simple">{{ errors.note }}
                        </Message>
                    </div>
                </div>
            </div>
        </div>

        <template #footer>
            <div v-if="mode === 'edit'" class="flex items-center justify-end gap-2 pt-4 border-t border-surface-200 dark:border-surface-700">
                <Button size="large" :fluid="false" :label="t('common.labels.cancel')" outlined @click="cancel" />
                <Button size="large" :fluid="false" :label="t('common.labels.save')" :loading="generateLoading"
                    @click="onUpdate" />
            </div>
            <div v-if="mode === 'static'" class="flex items-center justify-end gap-2 pt-4 border-t border-surface-200 dark:border-surface-700">
                <Button size="large" :fluid="false" :label="t('common.labels.cancel')" outlined @click="cancel" />
                <Button size="large" :fluid="false" type="submit" :label="t('common.labels.create')" :disabled="!content"
                    :loading="generateLoading" @click="onCreate" />
            </div>
        </template>
    </Drawer>

    <!-- Post-creation modal: download + save as template. -->
    <Dialog v-model:visible="resultDialog" modal @hide="resetState" :header="t('qrcodes.create.title')" :style="{ width: '25rem' }">
        <div v-if="!additional" class="flex flex-col gap-4">
            <div class="flex justify-center">
                <img class="max-w-64" :src="createdQRCode?.qrcode_base64" alt="QR Code" />
            </div>
            <div class="flex items-center">
                <div class="flex-1 flex justify-center">
                    <SplitButton size="large" :label="t('common.labels.download')" @click="downloadQRCode('png')"
                        :model="downloadItems" />
                </div>
                <div>
                    <Button icon="pi pi-plus" size="large" v-tooltip.bottom="t('common.labels.new')"
                        :disabled="!user.permissions.add_qrcode_template" @click="additional = true" />
                </div>
            </div>
        </div>
        <div v-else>
            <Qrcodes qrcodeTemplateCreate :shortlink="qrContent" @closeDialog="additional = false" />
        </div>
    </Dialog>
</template>
